import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { AuthContext } from '../../../contexts/AuthContext';
import PageTitle from '../../../components/PageTitle/PageTitle';
import AlertModal from '../../../components/AlertModal/AlertModal';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import Button from '../../../components/Button/Button';
import Table from '../../../components/Table/Table';
import Card from '../../../components/Card/Card';
import StatCard from '../../../components/StatCard/StatCard';
import { Modal } from '../../../components/Modal/Modal';
import {
  DynamicInput,
  DynamicSelect,
  DynamicCheckbox,
  DynamicTextarea,
  DynamicFileInput,
  DynamicImagePreview,
} from '../../../components/Inputs/Inputs';
import { ReactComponent as Edit } from '../../../assets/edit-3.svg';
import { ReactComponent as Close } from '../../../assets/x.svg';
import './RevenueConfig.scss';

const TABS = [
  { value: 'settings', label: 'Definições' },
  { value: 'catalog', label: 'Catálogo' },
  { value: 'opportunities', label: 'Oportunidades' },
  { value: 'analytics', label: 'Analytics' },
];

const CATEGORY_SUGGESTIONS = [
  'Late Check-out',
  'Early Check-in',
  'Upgrade de quarto',
  'Pequeno-almoço',
  'Parking',
  'Transfer',
  'SPA',
  'Massagens',
  'Restaurante',
  'Room Service',
  'Welcome Package',
  'Experiências',
];

const STATE_LABEL = {
  DETECTED: 'Detetada',
  OFFERED: 'Oferecida',
  INTERESTED: 'Interessado',
  CONTACT_REQUESTED: 'Contacto pedido',
  CONFIRMED: 'Confirmada',
  DECLINED: 'Recusada',
  EXPIRED: 'Expirada',
  CANCELLED: 'Cancelada',
};

function extractErrorMessage(err, fallback = 'Ocorreu um erro') {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    return detail.message || detail.error_code || fallback;
  }
  return detail || err?.message || fallback;
}

const emptyProductForm = {
  category: '',
  name_pt: '',
  name_en: '',
  name_es: '',
  short_description_pt: '',
  short_description_en: '',
  short_description_es: '',
  description_pt: '',
  description_en: '',
  description_es: '',
  price: '',
  price_is_from: false,
  currency: 'EUR',
  vat_rate: '',
  active: true,
  priority: 0,
  requires_confirmation: true,
  always_available: true,
  available_days: '',
  available_hours: '',
  external_link: '',
  contact_whatsapp: '',
  contact_phone: '',
  contact_email: '',
  enable_direct_interest: true,
  enable_handoff: false,
  enable_whatsapp: false,
  enable_phone: false,
  enable_external_link: false,
};

export default function RevenueConfig() {
  const { axiosAuth, currentUser, accessibleHotels = [] } = useContext(AuthContext);
  const isSuper = currentUser?.role === 'superadmin';

  const [allHotels, setAllHotels] = useState([]);
  const [groupNameById, setGroupNameById] = useState({});
  const [hotelId, setHotelId] = useState('');
  const [tab, setTab] = useState('settings');

  const [license, setLicense] = useState(null);
  const [licenseSaving, setLicenseSaving] = useState(false);
  const [notLicensed, setNotLicensed] = useState(false);

  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const [products, setProducts] = useState([]);
  const [productModal, setProductModal] = useState({
    open: false,
    mode: 'create',
    form: emptyProductForm,
    imageFile: null,
    previewUrl: '',
    intents: [],
    newIntentText: '',
    aiSuggestions: [],
    suggesting: false,
  });

  const [opportunities, setOpportunities] = useState([]);
  const [oppStateFilter, setOppStateFilter] = useState('');
  const [confirmModal, setConfirmModal] = useState({ open: false, opportunity: null, value: '' });
  const [confirmDeactivate, setConfirmDeactivate] = useState({ open: false, product: null });

  const [analytics, setAnalytics] = useState(null);

  const [errorModal, setErrorModal] = useState({ open: false, title: '', message: '' });
  const openError = useCallback((title, err) => {
    setErrorModal({ open: true, title, message: extractErrorMessage(err) });
  }, []);
  const closeError = useCallback(
    () => setErrorModal({ open: false, title: '', message: '' }),
    [],
  );
  const [infoModal, setInfoModal] = useState({ open: false, title: '', message: '' });

  // --- hotel scope (mesmo padrão de HandoffConfig/CsatConfig) ---
  useEffect(() => {
    if (!isSuper) return;
    (async () => {
      try {
        const [{ data: hotelsData }, { data: groupsData }] = await Promise.all([
          axiosAuth.get('/hotels'),
          axiosAuth.get('/groups'),
        ]);
        const nameByGroup = {};
        (groupsData || []).forEach((g) => {
          nameByGroup[g.id] = g.name;
        });
        setGroupNameById(nameByGroup);
        setAllHotels(hotelsData || []);
      } catch (err) {
        openError('Erro ao carregar hotéis', err);
      }
    })();
  }, [isSuper, axiosAuth, openError]);

  const hotelOptions = useMemo(() => {
    if (isSuper) {
      return allHotels.map((h) => ({
        value: String(h.id),
        label: groupNameById[h.group_id] ? `${h.name} — ${groupNameById[h.group_id]}` : h.name,
      }));
    }
    return accessibleHotels.map((h) => ({ value: String(h.id), label: h.name }));
  }, [isSuper, allHotels, groupNameById, accessibleHotels]);

  useEffect(() => {
    if (!hotelId && hotelOptions.length === 1) {
      setHotelId(hotelOptions[0].value);
    }
  }, [hotelOptions, hotelId]);

  const fetchLicense = useCallback(async () => {
    if (!isSuper || !hotelId) return;
    try {
      const { data } = await axiosAuth.get('/modules/status', {
        params: { hotel_id: Number(hotelId), module_code: 'AI_REVENUE_AGENT' },
      });
      setLicense(data?.modules?.[0] || null);
    } catch (err) {
      openError('Erro ao consultar licenciamento', err);
    }
  }, [isSuper, hotelId, axiosAuth, openError]);

  const fetchAll = useCallback(async () => {
    if (!hotelId) return;
    try {
      const [{ data: settingsData }, { data: productsData }] = await Promise.all([
        axiosAuth.get('/revenue/config/settings', { params: { hotel_id: Number(hotelId) } }),
        axiosAuth.get('/revenue/config/products', { params: { hotel_id: Number(hotelId) } }),
      ]);
      setSettingsForm({
        contact_whatsapp: settingsData.contact_whatsapp || '',
        contact_phone: settingsData.contact_phone || '',
        contact_email: settingsData.contact_email || '',
      });
      setProducts(productsData || []);
      setNotLicensed(false);
    } catch (err) {
      if (err?.response?.data?.detail?.error_code === 'MODULE_NOT_LICENSED') {
        setNotLicensed(true);
        setSettingsForm(null);
        setProducts([]);
      } else {
        openError('Erro ao carregar configuração do Upselling', err);
      }
    }
  }, [hotelId, axiosAuth, openError]);

  const fetchOpportunities = useCallback(async () => {
    if (!hotelId || notLicensed) return;
    try {
      const params = { hotel_id: Number(hotelId) };
      if (oppStateFilter) params.state = oppStateFilter;
      const { data } = await axiosAuth.get('/revenue/admin/opportunities', { params });
      setOpportunities(data || []);
    } catch (err) {
      openError('Erro ao carregar oportunidades', err);
    }
  }, [hotelId, notLicensed, oppStateFilter, axiosAuth, openError]);

  const fetchAnalytics = useCallback(async () => {
    if (!hotelId || notLicensed) return;
    try {
      const { data } = await axiosAuth.get('/revenue/admin/analytics', {
        params: { hotel_id: Number(hotelId) },
      });
      setAnalytics(data);
    } catch (err) {
      openError('Erro ao carregar analytics', err);
    }
  }, [hotelId, notLicensed, axiosAuth, openError]);

  useEffect(() => {
    setSettingsForm(null);
    setProducts([]);
    setOpportunities([]);
    setAnalytics(null);
    setNotLicensed(false);
    setLicense(null);
    if (!hotelId) return;
    fetchLicense();
    fetchAll();
  }, [hotelId, fetchLicense, fetchAll]);

  useEffect(() => {
    if (tab === 'opportunities') fetchOpportunities();
    if (tab === 'analytics') fetchAnalytics();
  }, [tab, fetchOpportunities, fetchAnalytics]);

  async function toggleLicense(enabled) {
    setLicenseSaving(true);
    try {
      await axiosAuth.post('/modules/licenses', {
        module_code: 'AI_REVENUE_AGENT',
        hotel_id: Number(hotelId),
        enabled,
        reason: 'manual',
      });
      await fetchLicense();
      await fetchAll();
      setInfoModal({
        open: true,
        title: enabled ? 'Módulo licenciado' : 'Módulo desativado',
        message: enabled
          ? 'O AI Revenue Agent foi licenciado para este hotel.'
          : 'O AI Revenue Agent foi desativado para este hotel.',
      });
    } catch (err) {
      openError('Erro ao alterar licenciamento', err);
    } finally {
      setLicenseSaving(false);
    }
  }

  async function saveSettings() {
    if (!settingsForm) return;
    setSavingSettings(true);
    try {
      await axiosAuth.put('/revenue/config/settings', settingsForm, {
        params: { hotel_id: Number(hotelId) },
      });
      setInfoModal({ open: true, title: 'Guardado', message: 'Definições atualizadas.' });
    } catch (err) {
      openError('Erro ao guardar definições', err);
    } finally {
      setSavingSettings(false);
    }
  }

  // --- catálogo ---
  function openCreateProduct() {
    setProductModal({
      open: true,
      mode: 'create',
      form: emptyProductForm,
      imageFile: null,
      previewUrl: '',
      intents: [],
      newIntentText: '',
      aiSuggestions: [],
      suggesting: false,
    });
  }

  async function openEditProduct(p) {
    try {
      const { data } = await axiosAuth.get(`/revenue/config/products/${p.id}`, {
        params: { hotel_id: Number(hotelId) },
      });
      setProductModal({
        open: true,
        mode: 'edit',
        id: data.id,
        form: {
          category: data.category || '',
          name_pt: data.name_pt,
          name_en: data.name_en,
          name_es: data.name_es,
          short_description_pt: data.short_description_pt || '',
          short_description_en: data.short_description_en || '',
          short_description_es: data.short_description_es || '',
          description_pt: data.description_pt || '',
          description_en: data.description_en || '',
          description_es: data.description_es || '',
          price: data.price ?? '',
          price_is_from: data.price_is_from,
          currency: data.currency,
          vat_rate: data.vat_rate ?? '',
          active: data.active,
          priority: data.priority,
          requires_confirmation: data.requires_confirmation,
          always_available: data.always_available,
          available_days: data.available_days || '',
          available_hours: data.available_hours || '',
          external_link: data.external_link || '',
          contact_whatsapp: data.contact_whatsapp || '',
          contact_phone: data.contact_phone || '',
          contact_email: data.contact_email || '',
          enable_direct_interest: data.enable_direct_interest,
          enable_handoff: data.enable_handoff,
          enable_whatsapp: data.enable_whatsapp,
          enable_phone: data.enable_phone,
          enable_external_link: data.enable_external_link,
        },
        imageFile: null,
        previewUrl: data.image_url || '',
        intents: data.intents || [],
        newIntentText: '',
        aiSuggestions: [],
        suggesting: false,
      });
    } catch (err) {
      openError('Erro ao carregar produto', err);
    }
  }

  function handleProductImage(e) {
    const file = e.target.files?.[0] || null;
    setProductModal((p) => ({
      ...p,
      imageFile: file,
      previewUrl: file ? URL.createObjectURL(file) : p.previewUrl,
    }));
  }

  async function saveProduct() {
    const { mode, form, id, imageFile } = productModal;
    if (!form.name_pt.trim() || !form.name_en.trim() || !form.name_es.trim()) {
      openError('Validação', new Error('Preencha o nome nas 3 línguas.'));
      return;
    }
    const payload = {
      ...form,
      price: form.price === '' ? null : Number(form.price),
      vat_rate: form.vat_rate === '' ? null : Number(form.vat_rate),
      priority: Number(form.priority) || 0,
    };
    try {
      let productId = id;
      if (mode === 'create') {
        const { data } = await axiosAuth.post(
          '/revenue/config/products',
          { hotel_id: Number(hotelId), ...payload },
          { params: { hotel_id: Number(hotelId) } },
        );
        productId = data.id;
      } else {
        await axiosAuth.patch(`/revenue/config/products/${id}`, payload, {
          params: { hotel_id: Number(hotelId) },
        });
      }
      if (imageFile) {
        const fd = new FormData();
        fd.append('image', imageFile);
        await axiosAuth.post(`/revenue/config/products/${productId}/image`, fd, {
          params: { hotel_id: Number(hotelId) },
        });
      }
      setProductModal((p) => ({ ...p, open: false }));
      await fetchAll();
    } catch (err) {
      openError('Erro ao guardar produto', err);
    }
  }

  function askDeactivateProduct(p) {
    setConfirmDeactivate({ open: true, product: p });
  }

  async function confirmDeactivateProduct() {
    const p = confirmDeactivate.product;
    try {
      await axiosAuth.post(`/revenue/config/products/${p.id}/deactivate`, null, {
        params: { hotel_id: Number(hotelId) },
      });
      await fetchAll();
    } catch (err) {
      openError('Erro ao desativar produto', err);
    } finally {
      setConfirmDeactivate({ open: false, product: null });
    }
  }

  async function activateProduct(p) {
    try {
      await axiosAuth.post(`/revenue/config/products/${p.id}/activate`, null, {
        params: { hotel_id: Number(hotelId) },
      });
      await fetchAll();
    } catch (err) {
      openError('Erro ao ativar produto', err);
    }
  }

  async function addIntent() {
    const text = productModal.newIntentText.trim();
    if (!text || !productModal.id) return;
    try {
      const { data } = await axiosAuth.post(
        `/revenue/config/products/${productModal.id}/intents`,
        { text },
        { params: { hotel_id: Number(hotelId) } },
      );
      setProductModal((p) => ({
        ...p,
        intents: [...p.intents, data],
        newIntentText: '',
        aiSuggestions: p.aiSuggestions.filter((s) => s !== text),
      }));
    } catch (err) {
      openError('Erro ao adicionar contexto', err);
    }
  }

  async function removeIntent(intentId) {
    try {
      await axiosAuth.delete(`/revenue/config/intents/${intentId}`, {
        params: { hotel_id: Number(hotelId) },
      });
      setProductModal((p) => ({ ...p, intents: p.intents.filter((i) => i.id !== intentId) }));
    } catch (err) {
      openError('Erro ao remover contexto', err);
    }
  }

  async function suggestIntents() {
    if (!productModal.id) return;
    setProductModal((p) => ({ ...p, suggesting: true }));
    try {
      const { data } = await axiosAuth.post(
        `/revenue/config/products/${productModal.id}/ai-opportunities`,
        {},
        { params: { hotel_id: Number(hotelId) } },
      );
      setProductModal((p) => ({ ...p, aiSuggestions: data.suggestions || [], suggesting: false }));
    } catch (err) {
      setProductModal((p) => ({ ...p, suggesting: false }));
      openError('Erro ao gerar sugestões', err);
    }
  }

  async function addSuggestedIntent(text) {
    if (!productModal.id) return;
    try {
      const { data } = await axiosAuth.post(
        `/revenue/config/products/${productModal.id}/intents`,
        { text },
        { params: { hotel_id: Number(hotelId) } },
      );
      setProductModal((p) => ({
        ...p,
        intents: [...p.intents, data],
        aiSuggestions: p.aiSuggestions.filter((s) => s !== text),
      }));
    } catch (err) {
      openError('Erro ao adicionar contexto', err);
    }
  }

  // --- oportunidades ---
  function openConfirmModal(opp) {
    setConfirmModal({ open: true, opportunity: opp, value: opp.price_potential || '' });
  }

  async function saveConfirmOpportunity() {
    const { opportunity, value } = confirmModal;
    if (!value) {
      openError('Validação', new Error('Indique o valor final.'));
      return;
    }
    try {
      await axiosAuth.post(
        `/revenue/admin/opportunities/${opportunity.id}/confirm`,
        { value_final: Number(value) },
        { params: { hotel_id: Number(hotelId) } },
      );
      setConfirmModal({ open: false, opportunity: null, value: '' });
      await fetchOpportunities();
    } catch (err) {
      openError('Erro ao confirmar oportunidade', err);
    }
  }

  async function declineOpportunity(opp) {
    try {
      await axiosAuth.post(
        `/revenue/admin/opportunities/${opp.id}/transition`,
        { state: 'DECLINED' },
        { params: { hotel_id: Number(hotelId) } },
      );
      await fetchOpportunities();
    } catch (err) {
      openError('Erro ao atualizar oportunidade', err);
    }
  }

  const productColumns = useMemo(
    () => [
      { key: 'name_pt', label: 'Nome' },
      { key: 'category', label: 'Categoria', render: (p) => p.category || '—' },
      {
        key: 'price',
        label: 'Preço',
        render: (p) => (p.price != null ? `${p.price_is_from ? 'desde ' : ''}${p.price} ${p.currency}` : '—'),
      },
      { key: 'active', label: 'Ativo', render: (p) => (p.active ? 'Sim' : 'Não') },
      {
        key: 'actions',
        label: 'Ações',
        render: (p) => (
          <>
            <button className="action-button edit" onClick={() => openEditProduct(p)}>
              <Edit />
            </button>
            {p.active ? (
              <button className="action-button delete" onClick={() => askDeactivateProduct(p)}>
                <Close />
              </button>
            ) : (
              <Button type="button" variant="secondary" onClick={() => activateProduct(p)}>
                Ativar
              </Button>
            )}
          </>
        ),
      },
    ],
    [],
  );

  const opportunityColumns = useMemo(
    () => [
      { key: 'product_name', label: 'Produto' },
      { key: 'state', label: 'Estado', render: (o) => STATE_LABEL[o.state] || o.state },
      {
        key: 'price_potential',
        label: 'Valor potencial',
        render: (o) => (o.price_potential != null ? `${o.price_potential} ${o.currency}` : '—'),
      },
      {
        key: 'value_final',
        label: 'Valor confirmado',
        render: (o) => (o.value_final != null ? `${o.value_final} ${o.currency}` : '—'),
      },
      {
        key: 'created_at',
        label: 'Data',
        render: (o) => (o.created_at ? new Date(o.created_at).toLocaleString() : '—'),
      },
      {
        key: 'actions',
        label: 'Ações',
        render: (o) =>
          ['OFFERED', 'INTERESTED', 'CONTACT_REQUESTED'].includes(o.state) ? (
            <>
              <Button type="button" onClick={() => openConfirmModal(o)}>
                Confirmar
              </Button>
              <Button type="button" variant="secondary" onClick={() => declineOpportunity(o)}>
                Recusar
              </Button>
            </>
          ) : (
            '—'
          ),
      },
    ],
    [],
  );

  return (
    <div className="revenue-config-page">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || 'Ocorreu um erro'}
          message={errorModal.message}
          onClose={closeError}
        />
      )}
      {infoModal.open && (
        <AlertModal
          variant="info"
          title={infoModal.title}
          message={infoModal.message}
          onClose={() => setInfoModal({ open: false, title: '', message: '' })}
        />
      )}
      {confirmDeactivate.open && (
        <ConfirmationModal
          title="Desativar produto"
          variant="danger"
          message="Deseja desativar este produto? Deixa de poder ser oferecido, mas não é apagado."
          secText="Fechar"
          varText="Desativar"
          onCancel={() => setConfirmDeactivate({ open: false, product: null })}
          onConfirm={confirmDeactivateProduct}
        />
      )}

      <PageTitle link="" title="Configurar Upselling" />

      {hotelOptions.length > 1 && (
        <div className="hotel-selector">
          <DynamicSelect
            label="Hotel"
            placeholder="Escolher um hotel"
            value={hotelId}
            onChange={(e) => setHotelId(e.target.value)}
            options={hotelOptions}
          />
        </div>
      )}

      {!hotelId ? (
        <div className="empty-page">
          <p>Escolhe um hotel para configurar o Upselling.</p>
        </div>
      ) : (
        <>
          {isSuper && (
            <div className="license-panel">
              <div className="license-status">
                <span className={`badge ${license?.licensed ? 'on' : 'off'}`}>
                  {license?.licensed ? 'Módulo licenciado' : 'Módulo não licenciado'}
                </span>
              </div>
              <Button
                type="button"
                variant={license?.licensed ? 'secondary' : 'primary'}
                disabled={licenseSaving}
                onClick={() => toggleLicense(!license?.licensed)}
              >
                {license?.licensed ? 'Desativar módulo' : 'Licenciar módulo'}
              </Button>
            </div>
          )}

          {notLicensed ? (
            <div className="empty-page">
              <p>
                {isSuper
                  ? 'Licencia o módulo AI Revenue Agent acima para configurar este hotel.'
                  : 'Este hotel não tem o módulo AI Revenue Agent licenciado. Fala com um SuperAdmin para o ativar.'}
              </p>
            </div>
          ) : (
            <>
              <div className="tabs">
                {TABS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    className={`tab-btn ${tab === t.value ? 'active' : ''}`}
                    onClick={() => setTab(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {tab === 'settings' && settingsForm && (
                <div className="settings-panel">
                  <p className="help">
                    Contacto geral de upselling deste hotel — usado quando um produto não tem
                    contacto próprio configurado.
                  </p>
                  <div className="row three-col">
                    <DynamicInput
                      type="text"
                      label="WhatsApp"
                      name="contact_whatsapp"
                      value={settingsForm.contact_whatsapp}
                      onChange={(name, value) =>
                        setSettingsForm((p) => ({ ...p, contact_whatsapp: value }))
                      }
                      disabled={savingSettings}
                    />
                    <DynamicInput
                      type="text"
                      label="Telefone"
                      name="contact_phone"
                      value={settingsForm.contact_phone}
                      onChange={(name, value) =>
                        setSettingsForm((p) => ({ ...p, contact_phone: value }))
                      }
                      disabled={savingSettings}
                    />
                    <DynamicInput
                      type="email"
                      label="Email"
                      name="contact_email"
                      value={settingsForm.contact_email}
                      onChange={(name, value) =>
                        setSettingsForm((p) => ({ ...p, contact_email: value }))
                      }
                      disabled={savingSettings}
                    />
                  </div>
                  <div className="settings-actions">
                    <Button type="button" onClick={saveSettings} disabled={savingSettings}>
                      Guardar definições
                    </Button>
                  </div>
                </div>
              )}

              {tab === 'catalog' && (
                <div className="section-panel">
                  <div className="section-header">
                    <Button type="button" onClick={openCreateProduct}>
                      Novo produto
                    </Button>
                  </div>
                  <div className="container">
                    <Table columns={productColumns} data={products} />
                  </div>
                </div>
              )}

              {tab === 'opportunities' && (
                <div className="section-panel">
                  <div className="section-header">
                    <div id="dynamic-select-wrapper">
                      <label className="dynamic-select-label">Estado</label>
                      <select
                        className="dynamic-select-field"
                        value={oppStateFilter}
                        onChange={(e) => setOppStateFilter(e.target.value)}
                      >
                        <option value="">Todos</option>
                        {Object.entries(STATE_LABEL).map(([k, label]) => (
                          <option key={k} value={k}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="container">
                    <Table columns={opportunityColumns} data={opportunities} />
                  </div>
                </div>
              )}

              {tab === 'analytics' && analytics && (
                <div className="section-panel">
                  <div className="stat-grid">
                    <StatCard label="Oportunidades" value={analytics.funnel.detected} />
                    <StatCard label="Ofertas" value={analytics.funnel.offered} />
                    <StatCard label="Interesses" value={analytics.funnel.interested} />
                    <StatCard label="Confirmadas" value={analytics.funnel.confirmed} />
                    <StatCard
                      label="Taxa de conversão"
                      value={analytics.conversion_rate != null ? `${analytics.conversion_rate}%` : '—'}
                    />
                    <StatCard
                      label="Receita confirmada"
                      value={`${analytics.revenue_confirmed} €`}
                    />
                    <StatCard
                      label="Receita potencial"
                      value={`${analytics.revenue_potential} €`}
                    />
                    <StatCard
                      label="Ticket médio"
                      value={analytics.avg_ticket != null ? `${analytics.avg_ticket} €` : '—'}
                    />
                  </div>

                  <Card title="Funil comercial">
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart
                        data={[
                          { label: 'Oportunidades', value: analytics.funnel.detected },
                          { label: 'Ofertas', value: analytics.funnel.offered },
                          { label: 'Interesses', value: analytics.funnel.interested },
                          { label: 'Contactos', value: analytics.funnel.contact_requested },
                          { label: 'Confirmadas', value: analytics.funnel.confirmed },
                        ]}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Line type="monotone" dataKey="value" stroke="#2198f3" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>

                  <Card title="Performance por produto">
                    <Table
                      columns={[
                        { key: 'product_name', label: 'Produto' },
                        { key: 'opportunities', label: 'Oportunidades' },
                        { key: 'interests', label: 'Interesses' },
                        { key: 'confirmed', label: 'Confirmadas' },
                        {
                          key: 'revenue_confirmed',
                          label: 'Receita',
                          render: (r) => `${r.revenue_confirmed} €`,
                        },
                        {
                          key: 'conversion_rate',
                          label: 'Conversão',
                          render: (r) => (r.conversion_rate != null ? `${r.conversion_rate}%` : '—'),
                        },
                      ]}
                      data={analytics.by_product}
                    />
                  </Card>

                  <Card title="Oportunidades perdidas">
                    {analytics.lost_opportunities.length === 0 ? (
                      <div className="empty">Sem dados.</div>
                    ) : (
                      <Table
                        columns={[
                          { key: 'product_name', label: 'Produto' },
                          { key: 'state', label: 'Estado', render: (r) => STATE_LABEL[r.state] || r.state },
                          {
                            key: 'created_at',
                            label: 'Data',
                            render: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'),
                          },
                        ]}
                        data={analytics.lost_opportunities}
                      />
                    )}
                  </Card>
                </div>
              )}
            </>
          )}
        </>
      )}

      {productModal.open && (
        <Modal
          isVisible={productModal.open}
          onClose={() => setProductModal((p) => ({ ...p, open: false }))}
          title={productModal.mode === 'create' ? 'Novo produto' : `Editar ${productModal.form.name_pt}`}
          onSave={saveProduct}
          saveText={productModal.mode === 'create' ? 'Criar' : 'Guardar'}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveProduct();
            }}
          >
            <div className="row">
              <DynamicImagePreview imageSrc={productModal.previewUrl} />
            </div>
            <div className="row">
              <DynamicFileInput name="image" label="Imagem:" onChange={handleProductImage} />
            </div>

            <div className="row two-col">
              <DynamicInput
                type="text"
                label="Categoria"
                name="category"
                value={productModal.form.category}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, category: value } }))
                }
                placeholder={CATEGORY_SUGGESTIONS.join(', ')}
              />
              <DynamicInput
                type="number"
                label="Prioridade"
                name="priority"
                value={productModal.form.priority}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, priority: value } }))
                }
              />
            </div>

            <h3>Nome</h3>
            <div className="row three-col">
              {['pt', 'en', 'es'].map((lang) => (
                <DynamicInput
                  key={lang}
                  type="text"
                  label={lang.toUpperCase()}
                  name={`name_${lang}`}
                  required
                  value={productModal.form[`name_${lang}`]}
                  onChange={(name, value) =>
                    setProductModal((p) => ({ ...p, form: { ...p.form, [name]: value } }))
                  }
                />
              ))}
            </div>

            <h3>Descrição curta</h3>
            <div className="row three-col">
              {['pt', 'en', 'es'].map((lang) => (
                <DynamicInput
                  key={lang}
                  type="text"
                  label={lang.toUpperCase()}
                  name={`short_description_${lang}`}
                  value={productModal.form[`short_description_${lang}`]}
                  onChange={(name, value) =>
                    setProductModal((p) => ({ ...p, form: { ...p.form, [name]: value } }))
                  }
                />
              ))}
            </div>

            <h3>Descrição comercial</h3>
            <div className="row three-col">
              {['pt', 'en', 'es'].map((lang) => (
                <DynamicTextarea
                  key={lang}
                  rows={2}
                  label={lang.toUpperCase()}
                  name={`description_${lang}`}
                  value={productModal.form[`description_${lang}`]}
                  onChange={(name, value) =>
                    setProductModal((p) => ({ ...p, form: { ...p.form, [name]: value } }))
                  }
                />
              ))}
            </div>

            <h3>Preço</h3>
            <div className="row four-col">
              <DynamicInput
                type="number"
                label="Preço"
                name="price"
                value={productModal.form.price}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, price: value } }))
                }
              />
              <DynamicInput
                type="text"
                label="Moeda"
                name="currency"
                value={productModal.form.currency}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, currency: value } }))
                }
              />
              <DynamicInput
                type="number"
                label="IVA (%)"
                name="vat_rate"
                value={productModal.form.vat_rate}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, vat_rate: value } }))
                }
              />
              <div className="row checkbox-row">
                <DynamicCheckbox
                  label='Mostrar como "Desde"'
                  name="price_is_from"
                  checked={productModal.form.price_is_from}
                  onChange={(name, value) =>
                    setProductModal((p) => ({ ...p, form: { ...p.form, price_is_from: value } }))
                  }
                />
              </div>
            </div>

            <h3>Disponibilidade</h3>
            <div className="row checkbox-row">
              <DynamicCheckbox
                label="Sempre disponível"
                name="always_available"
                checked={productModal.form.always_available}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, always_available: value } }))
                }
              />
            </div>
            {!productModal.form.always_available && (
              <div className="row two-col">
                <DynamicInput
                  type="text"
                  label="Dias (ex.: mon,tue,wed)"
                  name="available_days"
                  value={productModal.form.available_days}
                  onChange={(name, value) =>
                    setProductModal((p) => ({ ...p, form: { ...p.form, available_days: value } }))
                  }
                />
                <DynamicInput
                  type="text"
                  label="Horas (ex.: 09:00-18:00)"
                  name="available_hours"
                  value={productModal.form.available_hours}
                  onChange={(name, value) =>
                    setProductModal((p) => ({ ...p, form: { ...p.form, available_hours: value } }))
                  }
                />
              </div>
            )}

            <h3>Contacto do produto (opcional — sem isto usa o contacto geral do hotel)</h3>
            <div className="row three-col">
              <DynamicInput
                type="text"
                label="WhatsApp"
                name="contact_whatsapp"
                value={productModal.form.contact_whatsapp}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, contact_whatsapp: value } }))
                }
              />
              <DynamicInput
                type="text"
                label="Telefone"
                name="contact_phone"
                value={productModal.form.contact_phone}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, contact_phone: value } }))
                }
              />
              <DynamicInput
                type="email"
                label="Email"
                name="contact_email"
                value={productModal.form.contact_email}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, contact_email: value } }))
                }
              />
            </div>
            <div className="row">
              <DynamicInput
                type="text"
                label="Link externo"
                name="external_link"
                value={productModal.form.external_link}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, external_link: value } }))
                }
              />
            </div>

            <h3>Modos de conversão</h3>
            <div className="row checkbox-grid-row">
              <DynamicCheckbox
                label="Tenho interesse"
                name="enable_direct_interest"
                checked={productModal.form.enable_direct_interest}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, enable_direct_interest: value } }))
                }
              />
              <DynamicCheckbox
                label="Falar com um operador"
                name="enable_handoff"
                checked={productModal.form.enable_handoff}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, enable_handoff: value } }))
                }
              />
              <DynamicCheckbox
                label="WhatsApp"
                name="enable_whatsapp"
                checked={productModal.form.enable_whatsapp}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, enable_whatsapp: value } }))
                }
              />
              <DynamicCheckbox
                label="Telefone"
                name="enable_phone"
                checked={productModal.form.enable_phone}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, enable_phone: value } }))
                }
              />
              <DynamicCheckbox
                label="Link externo"
                name="enable_external_link"
                checked={productModal.form.enable_external_link}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, enable_external_link: value } }))
                }
              />
            </div>
            <div className="row checkbox-row">
              <DynamicCheckbox
                label="Requer confirmação (preço/disponibilidade não são finais)"
                name="requires_confirmation"
                checked={productModal.form.requires_confirmation}
                onChange={(name, value) =>
                  setProductModal((p) => ({ ...p, form: { ...p.form, requires_confirmation: value } }))
                }
              />
            </div>

            {productModal.mode === 'edit' && (
              <>
                <h3>Contextos (intents) — usados pela deteção de IA</h3>
                <div className="intents-list">
                  {productModal.intents.map((i) => (
                    <span className="intent-chip" key={i.id}>
                      {i.text}
                      <button type="button" onClick={() => removeIntent(i.id)}>
                        ×
                      </button>
                    </span>
                  ))}
                  {productModal.intents.length === 0 && (
                    <span className="help">Sem contextos definidos ainda.</span>
                  )}
                </div>
                <div className="row two-col">
                  <DynamicInput
                    type="text"
                    label="Novo contexto"
                    name="newIntentText"
                    value={productModal.newIntentText}
                    onChange={(name, value) =>
                      setProductModal((p) => ({ ...p, newIntentText: value }))
                    }
                  />
                  <div className="intent-add-actions">
                    <Button type="button" variant="secondary" onClick={addIntent}>
                      Adicionar
                    </Button>
                    <Button type="button" variant="secondary" onClick={suggestIntents} disabled={productModal.suggesting}>
                      Gerar com IA
                    </Button>
                  </div>
                </div>
                {productModal.aiSuggestions.length > 0 && (
                  <div className="intents-list suggestions">
                    {productModal.aiSuggestions.map((s) => (
                      <button
                        type="button"
                        className="intent-chip suggestion"
                        key={s}
                        onClick={() => addSuggestedIntent(s)}
                      >
                        + {s}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </form>
        </Modal>
      )}

      {confirmModal.open && (
        <Modal
          isVisible={confirmModal.open}
          onClose={() => setConfirmModal({ open: false, opportunity: null, value: '' })}
          title="Confirmar oportunidade"
          onSave={saveConfirmOpportunity}
          saveText="Confirmar"
        >
          <div className="row">
            <DynamicInput
              type="number"
              label="Valor final"
              name="value"
              required
              value={confirmModal.value}
              onChange={(name, value) => setConfirmModal((p) => ({ ...p, value }))}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}
